export interface Rect {
    x: number
    y: number
    width: number
    height: number
    entityId?: string
}

/**
 * Quadtree Constructor
 */
export default class Quadtree {
    max_objects: number
    max_levels: number
    level: number
    bounds: Rect
    objects: Rect[]
    nodes: Quadtree[]

    constructor(bounds: Rect, max_objects?: number, max_levels?: number, level?: number) {
        this.max_objects = max_objects || 10
        this.max_levels = max_levels || 4

        this.level = level || 0
        this.bounds = bounds

        this.objects = []
        this.nodes = []
    }

    /**
     * Split the node into 4 subnodes
     */
    split = () => {
        const nextLevel = this.level + 1,
            subWidth = this.bounds.width / 2,
            subHeight = this.bounds.height / 2,
            x = this.bounds.x,
            y = this.bounds.y

        //top right node
        this.nodes[0] = new Quadtree(
            {
                x: x + subWidth,
                y: y,
                width: subWidth,
                height: subHeight,
            },
            this.max_objects,
            this.max_levels,
            nextLevel,
        )

        //top left node
        this.nodes[1] = new Quadtree(
            {
                x: x,
                y: y,
                width: subWidth,
                height: subHeight,
            },
            this.max_objects,
            this.max_levels,
            nextLevel,
        )

        //bottom left node
        this.nodes[2] = new Quadtree(
            {
                x: x,
                y: y + subHeight,
                width: subWidth,
                height: subHeight,
            },
            this.max_objects,
            this.max_levels,
            nextLevel,
        )

        //bottom right node
        this.nodes[3] = new Quadtree(
            {
                x: x + subWidth,
                y: y + subHeight,
                width: subWidth,
                height: subHeight,
            },
            this.max_objects,
            this.max_levels,
            nextLevel,
        )
    }

    /**
     * Determine which node the object belongs to
     */
    getIndex = (pRect: Rect) => {
        const indexes: number[] = []
        const verticalMidpoint = this.bounds.x + this.bounds.width / 2
        const horizontalMidpoint = this.bounds.y + this.bounds.height / 2

        const startIsNorth = pRect.y < horizontalMidpoint
        const startIsWest = pRect.x < verticalMidpoint
        const endIsEast = pRect.x + pRect.width > verticalMidpoint
        const endIsSouth = pRect.y + pRect.height > horizontalMidpoint

        // Use bitwise operations for better performance
        //top-right quad
        if (startIsNorth && endIsEast) {
            indexes.push(0)
        }

        //top-left quad
        if (startIsWest && startIsNorth) {
            indexes.push(1)
        }

        //bottom-left quad
        if (startIsWest && endIsSouth) {
            indexes.push(2)
        }

        //bottom-right quad
        if (endIsEast && endIsSouth) {
            indexes.push(3)
        }

        return indexes
    }

    /**
     * Insert the object into the node. If the node
     * exceeds the capacity, it will split and add all
     * objects to their corresponding subnodes.
     */
    insert = (pRect: Rect) => {
        //if we have subnodes, call insert on matching subnodes
        if (this.nodes.length) {
            const indexes = this.getIndex(pRect)
            for (let i = 0; i < indexes.length; i++) {
                this.nodes[indexes[i]].insert(pRect)
            }
            return
        }

        //otherwise, store object here
        this.objects.push(pRect)

        //max_objects reached
        if (this.objects.length > this.max_objects && this.level < this.max_levels) {
            //   if we don't already have subnodes
            if (!this.nodes.length) {
                this.split()
            }

            //add all objects to their corresponding subnode
            const objectsToProcess = this.objects
            this.objects = [] // Clear immediately to free memory

            for (let i = 0; i < objectsToProcess.length; i++) {
                const indexes = this.getIndex(objectsToProcess[i])
                for (let k = 0; k < indexes.length; k++) {
                    this.nodes[indexes[k]].insert(objectsToProcess[i])
                }
            }
        }
    }

    /**
     * Remove the exact rectangle from every leaf containing it. Use its old bounds
     * before changing its position, since rectangles can span multiple nodes.
     */
    remove = (pRect: Rect): void => {
        if (this.nodes.length) {
            for (const index of this.getIndex(pRect)) {
                this.nodes[index].remove(pRect)
            }
            return
        }
        const index = this.objects.indexOf(pRect)
        if (index !== -1) {
            this.objects.splice(index, 1)
        }
    }

    /**
     * Return all objects that could collide with the given object
     */
    retrieve = (pRect: Rect) => {
        // Early return if no objects in this node
        if (this.objects.length === 0 && this.nodes.length === 0) {
            return []
        }

        let returnObjects = this.objects

        //if we have subnodes, retrieve their objects
        if (this.nodes.length) {
            const indexes = this.getIndex(pRect)
            for (let i = 0; i < indexes.length; i++) {
                const subObjects = this.nodes[indexes[i]].retrieve(pRect)
                if (subObjects.length > 0) {
                    returnObjects = returnObjects.concat(subObjects)
                }
            }
        }

        // Use Set for O(1) duplicate removal instead of O(n²) indexOf
        if (returnObjects.length > 1) {
            const seen = new Set<Rect>()
            const uniqueObjects: Rect[] = []
            for (const obj of returnObjects) {
                if (!seen.has(obj)) {
                    seen.add(obj)
                    uniqueObjects.push(obj)
                }
            }
            return uniqueObjects
        }

        return returnObjects
    }

    /**
     * Clear the quadtree
     */
    clear = () => {
        this.objects = []

        for (let i = 0; i < this.nodes.length; i++) {
            if (this.nodes.length) {
                this.nodes[i].clear()
            }
        }

        this.nodes = []
    }
}
